import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { History, Search, X } from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import PaginationFooter from '../../components/PaginationFooter';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../lib/format';

// Admin → Recent platform activity. Cross-tenant feed of user actions.
// Search matches the action verb (e.g. "rewrite", "audit_run") OR the
// actor's email/name.
export default function ActivityPage() {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const params = new URLSearchParams();
  if (query) params.set('q', query);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));

  const activityQuery = useQuery({
    queryKey: ['admin-activity', query, page, PAGE_SIZE],
    queryFn: () => api.get(`/api/admin/activity?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const activity = activityQuery.data?.activity || [];
  const total = activityQuery.data?.total ?? 0;
  const totalPages = activityQuery.data?.totalPages ?? 1;
  const hasActiveFilter = Boolean(query);

  return (
    <div className="card">
      <div className="section-header flex-wrap gap-3">
        <div className="section-title">
          <span className="section-icon">
            <History className="w-4 h-4" />
          </span>
          Recent platform activity
          {activityQuery.data && (
            <span className="text-xs text-ink-400 font-normal ml-1">({total})</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10 max-w-xs"
              placeholder="Search action or user…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {hasActiveFilter && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setSearchInput('');
                setQuery('');
              }}
              title="Clear search"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>
      {activityQuery.isLoading ? (
        <SkeletonTable rows={8} cols={2} wrap={false} />
      ) : !activity.length ? (
        <EmptyState
          icon={History}
          title={hasActiveFilter ? 'No activity matches.' : 'No activity yet.'}
          body={hasActiveFilter ? 'Try a different search term.' : undefined}
        />
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {activity.map((a) => (
            <li
              key={a._id}
              className="py-2.5 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <span className="pill bg-gray-100 text-gray-700 mr-2">{a.action}</span>
                <span className="text-ink-700">
                  {a.user_id?.email || '—'}
                  {a.domain_id?.domain ? ` · ${a.domain_id.domain}` : ''}
                </span>
              </div>
              <div className="text-ink-400 text-xs whitespace-nowrap">
                {formatDateTime(a.createdAt)}
              </div>
            </li>
          ))}
        </ul>
      )}

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        isFetching={activityQuery.isFetching}
      />
    </div>
  );
}
